import {
  crossArea,
  getAreas,
  type TripArea,
  addAgendaItem,
  getHere,
  getPlan,
  getWay,
  NotFound,
  Pick,
  setTrack,
  toIso,
  type Plan,
  type PlanStop,
  type Turn,
} from '@trippath/shared';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { MapPanel } from '../../components/map/MapPanel';
import { crossings, type Inside } from '../../lib/areas';
import type { MapData } from '../../lib/map';
import {
  ARRIVED_METRES,
  gpxOf,
  keepRecording,
  keptRecording,
  LONGEST_MS,
  metresApart,
  stays,
  stretches,
  totals,
  worthKeeping,
  type Fix,
  type Going,
  type Kept,
} from '../../lib/recording';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; stop: PlanStop; next: PlanStop | null };

const EMOJI: Record<Going, string> = { foot: '🚶', bike: '🚲', car: '🚗', train: '🚆' };

/** `1:12:40`: hours, minutes and seconds. */
function clock(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const two = (n: number) => String(n).padStart(2, '0');
  return `${String(Math.floor(seconds / 3600))}:${two(Math.floor(seconds / 60) % 60)}:${two(seconds % 60)}`;
}

/** `09:31`: a moment's time on this phone's clock, which is where it was. */
function hhmm(at: number): string {
  const when = new Date(at);
  return `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`;
}

/**
 * A way recorded on the phone (the `Record` mockup): started only when one presses it, its points
 * kept on this phone; how one goes found from the speed, arriving at the next stop told, walked to
 * the room by OSRM's turns; at the end looked over, and only what is ticked added to the trip.
 */
export function Record() {
  const { t } = useTranslation();
  const { id = '', stop: stopId = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    getPlan(id).then(
      (plan) => {
        if (!live) return;
        // A stop by its id, or by its place in the route as Today links to it.
        const at = /^\d+$/.test(stopId)
          ? Number(stopId)
          : plan.stops.findIndex((one) => one.id === stopId);
        const stop = plan.stops[at];
        setState(
          stop === undefined
            ? { kind: 'missing' }
            : { kind: 'ready', plan, stop, next: plan.stops[at + 1] ?? null },
        );
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, stopId]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  return <Recording trip={id} stop={state.stop} next={state.next} />;
}

/** Where the way ends: the next stop's room, when it has one with a place, else the stop itself. */
function endOf(next: PlanStop | null) {
  const room = next?.bookings.find((one) => one.kind === 'stay' && one.place !== null);
  if (room?.place) {
    return {
      lat: room.place.lat,
      lon: room.place.lon,
      name: room.name,
      checkIn: room.checkIn,
      nights: room.nights,
    };
  }
  return next?.place
    ? { lat: next.place.lat, lon: next.place.lon, name: null, checkIn: null, nights: next.days }
    : null;
}

/** Recording, guiding and looking it over. */
function Recording({
  trip,
  stop,
  next,
}: {
  trip: string;
  stop: PlanStop;
  next: PlanStop | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ownKept = keptRecording(trip);
  const resumed = ownKept?.stop === stop.id ? ownKept : null;
  const [asked, setPhase] = useState<'start' | 'on' | 'guide' | 'review'>(
    resumed && resumed.fixes.length > 0 ? 'review' : 'start',
  );
  const [going, setGoing] = useState<'detect' | Going>('detect');
  const [kept, setKept] = useState<Kept>(resumed ?? { stop: stop.id, began: 0, fixes: [] });
  const [paused, setPaused] = useState(false);
  const [lost, setLost] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [turns, setTurns] = useState<Turn[] | 'none' | null>(null);
  const [guideTo, setGuideTo] = useState('');
  const [said, setSaid] = useState<'added' | 'deleted' | 'failed' | null>(null);
  const end = endOf(next);
  // The trip's areas: crossing one, as this phone sees it, is told to the trip.
  const areas = useRef<TripArea[]>([]);
  const inside = useRef<Inside>({});
  useEffect(() => {
    getAreas(trip).then(
      (found) => {
        areas.current = found;
      },
      () => undefined,
    );
  }, [trip]);
  const fixes = kept.fixes;
  const here = fixes.at(-1);
  // Six hours on, it stops by itself and is looked over.
  const phase =
    asked === 'on' && kept.began > 0 && now - kept.began >= LONGEST_MS ? 'review' : asked;
  // Arriving: any point within 300 m of the next stop's room or point.
  const arrived = end !== null && fixes.some((fix) => metresApart(fix, end) <= ARRIVED_METRES);

  // The points, while recording: one kept every 10 m or 30 s, on this phone only.
  useEffect(() => {
    if ((phase !== 'on' && phase !== 'guide') || paused) return;
    const watch = navigator.geolocation.watchPosition(
      (found) => {
        setLost(false);
        const fix: Fix = {
          lat: found.coords.latitude,
          lon: found.coords.longitude,
          at: found.timestamp,
          alt: found.coords.altitude ?? null,
        };
        const seen = crossings(inside.current, areas.current, fix);
        inside.current = seen.inside;
        for (const one of seen.crossed) {
          crossArea(trip, one.id, one.arrived).then(
            () => undefined,
            () => undefined,
          );
        }
        setKept((was) => {
          if (!worthKeeping(was.fixes.at(-1), fix)) return was;
          const made = { ...was, fixes: [...was.fixes, fix] };
          keepRecording(trip, made);
          return made;
        });
      },
      () => {
        setLost(true);
      },
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
    return () => {
      navigator.geolocation.clearWatch(watch);
    };
  }, [phase, paused, trip]);

  // The screen kept on while recording, where the browser can.
  useEffect(() => {
    if (phase !== 'on' && phase !== 'guide') return;
    const held: { lock: { release: () => Promise<void> } | null } = { lock: null };
    const screen = 'wakeLock' in navigator ? navigator.wakeLock.request('screen') : null;
    void screen?.then(
      (got) => {
        held.lock = got;
      },
      () => undefined,
    );
    return () => {
      void held.lock?.release();
    };
  }, [phase]);

  // The clock while recording; six hours on, it stops by itself.
  useEffect(() => {
    if (phase !== 'on') return;
    const tick = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(tick);
    };
  }, [phase]);

  const parts = useMemo(() => stretches(fixes), [fixes]);
  const rests = useMemo(() => stays(fixes), [fixes]);
  const sum = totals(fixes);
  const km = (metres: number) =>
    `${(metres / 1000).toLocaleString(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('record.km')}`;
  const goingSaid = (one: Going) => t(`record.goingAs.${one}`).toLowerCase();

  const begin = () => {
    const made = { stop: stop.id, began: Date.now(), fixes: [] };
    setKept(made);
    keepRecording(trip, made);
    setSaid(null);
    setNow(Date.now());
    setPhase('on');
  };

  const guide = (from: Fix, to: { lat: number; lon: number; name: string }) => {
    setGuideTo(to.name);
    setTurns(null);
    setPhase('guide');
    getWay(from, to, 'foot').then(
      (road) => {
        setTurns(road && road.turns.length > 0 ? road.turns : 'none');
      },
      () => {
        setTurns('none');
      },
    );
  };

  return (
    <div className="page record-page">
      <header className="page-head">
        <h1>
          {next
            ? t('record.title', { from: stop.city, to: next.city })
            : t('record.titleAlone', { from: stop.city })}
        </h1>
        <p className="lead">{t('record.lead')}</p>
      </header>
      <div className="record-layout">
        <section
          className="panel record-phone"
          aria-label={t('record.title', { from: stop.city, to: next?.city ?? '' })}
        >
          {phase === 'start' && (
            <div className="record-body">
              <h2 className="record-caps">{t('record.how')}</h2>
              <ul className="record-how">
                <li>{t('record.how1')}</li>
                <li>{t('record.how2')}</li>
                <li>{t('record.how3')}</li>
                <li>{t('record.how4')}</li>
              </ul>
              <Pick
                label={t('record.going')}
                searchable={false}
                value={going}
                options={(['detect', 'foot', 'bike', 'car', 'train'] as const).map((one) => ({
                  value: one,
                  label: t(`record.goingAs.${one}`),
                }))}
                onChange={(value) => {
                  setGoing(value);
                }}
              />
              {said === 'deleted' && (
                <p className="quiet record-small" role="status">
                  {t('record.deleted')}
                </p>
              )}
              {said === 'added' && (
                <p className="record-said" role="status">
                  {t('record.added', { city: stop.city })}
                </p>
              )}
              <button type="button" className="button button-primary" onClick={begin}>
                {t('record.start')}
              </button>
              <p className="quiet record-small">{t('record.startHow')}</p>
            </div>
          )}
          {phase === 'on' && (
            <div className="record-body">
              <p className="record-top">
                <span>{next ? `${stop.city} → ${next.city}` : stop.city}</span>
                <span className="record-rec" role="timer">
                  {paused ? t('record.paused') : `● ${clock(now - kept.began)}`}
                </span>
              </p>
              {arrived && next && (
                <div className="record-toast" role="status">
                  <b>{t('record.arrived', { city: next.city })}</b>
                  <span>
                    {end.name && here
                      ? t('record.arrivedStay', {
                          nights: end.nights,
                          stay: end.name,
                          metres: Math.round(metresApart(here, end)),
                          time: end.checkIn ?? '—',
                        })
                      : t('record.arrivedNights', { nights: end.nights })}
                  </span>
                </div>
              )}
              {lost && (
                <p className="form-failed" role="alert">
                  {t('record.lost')}
                </p>
              )}
              <div className="record-big">
                <div>
                  <b>
                    {(sum.metres / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })}
                  </b>
                  <span>{t('record.km')}</span>
                </div>
                <div>
                  <b>{clock(now - kept.began).slice(0, -3)}</b>
                  <span>{t('record.elapsed')}</span>
                </div>
                <div>
                  <b>↑ {sum.ascent}</b>
                  <span>{t('record.up')}</span>
                </div>
              </div>
              {parts.length > 0 && (
                <div className="record-mode">
                  <span>
                    {t('record.now', {
                      going: goingSaid(
                        going === 'detect'
                          ? (parts.at(-1) as (typeof parts)[number]).going
                          : going,
                      ),
                    })}
                  </span>
                  {parts.length > 1 && (
                    <span className="quiet record-small">
                      {t('record.before', {
                        emoji: EMOJI[(parts.at(-2) as (typeof parts)[number]).going],
                        going: goingSaid((parts.at(-2) as (typeof parts)[number]).going),
                        km: km((parts.at(-2) as (typeof parts)[number]).metres),
                      })}
                    </span>
                  )}
                </div>
              )}
              <div className="record-row">
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setPaused(!paused);
                  }}
                >
                  {paused ? t('record.resume') : t('record.pause')}
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => {
                    setPhase('review');
                  }}
                >
                  {t('record.finish')}
                </button>
              </div>
              {arrived && end.name && (
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    // Arrived: the phone has been somewhere.
                    guide(fixes.at(-1) as Fix, {
                      lat: end.lat,
                      lon: end.lon,
                      name: end.name,
                    });
                  }}
                >
                  {t('record.guide', { stay: end.name })}
                </button>
              )}
            </div>
          )}
          {phase === 'guide' && (
            <Guiding
              turns={turns}
              here={fixes.at(-1) as Fix}
              stay={guideTo}
              onEnd={() => {
                setPhase('on');
              }}
            />
          )}
          {phase === 'review' && (
            <Review
              trip={trip}
              stop={stop}
              fixes={fixes}
              parts={parts}
              rests={rests}
              onDone={(how) => {
                setSaid(how);
                if (how === 'failed') return;
                keepRecording(trip, null);
                setKept({ stop: stop.id, began: 0, fixes: [] });
                setPhase('start');
              }}
              said={said}
            />
          )}
        </section>
        <div className="record-map">
          <MapPanel
            data={
              {
                points: next?.place
                  ? [
                      {
                        lat: next.place.lat,
                        lon: next.place.lon,
                        label: next.city,
                        kind: 'stop',
                      },
                    ]
                  : [],
                lines:
                  fixes.length >= 2
                    ? [
                        {
                          kind: 'walk',
                          path: fixes.map((fix): [number, number] => [fix.lon, fix.lat]),
                        },
                      ]
                    : [],
              } satisfies MapData
            }
            label={t('record.mapLabel')}
            cooperative={false}
            keepView
            closest={15}
            focus={here ?? null}
          />
        </div>
      </div>
      <details className="panel record-notes">
        <summary>{t('record.notes')}</summary>
        <ul>
          <li>{t('record.note1')}</li>
          <li>{t('record.note2')}</li>
          <li>{t('record.note3')}</li>
          <li>{t('record.note4')}</li>
          <li>{t('record.note5')}</li>
        </ul>
      </details>
    </div>
  );
}

/** A turn said: `Turn right: Sanjō-dōri`. */
function turnSaid(turn: Turn, t: TFunction): string {
  const kind = ['depart', 'arrive'].includes(turn.kind) ? turn.kind : turn.towards || 'other';
  const known = [
    'depart',
    'arrive',
    'straight',
    'left',
    'right',
    'slight left',
    'slight right',
    'sharp left',
    'sharp right',
    'uturn',
  ].includes(kind);
  const said = t(`record.turn.${known ? kind : 'other'}`);
  return turn.street === '' ? said : t('record.onto', { turn: said, street: turn.street });
}

/** Walked to the room by OSRM's turns: the next one, the one after it, and how far in all. */
function Guiding({
  turns,
  here,
  stay,
  onEnd,
}: {
  turns: Turn[] | 'none' | null;
  here: Fix;
  stay: string;
  onEnd: () => void;
}) {
  const { t } = useTranslation();
  const list = Array.isArray(turns) ? turns : [];
  const apart = (turn: Turn) => metresApart(here, { lon: turn.at[0], lat: turn.at[1] });
  // The turn nearest is the one ahead; once within 15 m of it, it is done and the next is.
  const nearest = list.reduce(
    (best, turn, at) => (apart(turn) < apart(list[best] as Turn) ? at : best),
    0,
  );
  const passed = list[nearest] && apart(list[nearest]) <= 15 ? nearest + 1 : nearest;
  const ahead = list[passed];
  const after = list[passed + 1];
  const toAhead = ahead ? Math.round(apart(ahead)) : 0;
  const rest = list.slice(passed).reduce((all, turn) => all + turn.metres, toAhead);
  return (
    <div className="record-guide">
      {turns === null && <p className="quiet record-body">{t('trips.loading')}</p>}
      {turns === 'none' && (
        <p className="record-body" role="status">
          {t('record.noGuide', { stay })}
        </p>
      )}
      {ahead && (
        <>
          <div className="record-turn" role="status">
            <b>{toAhead} m</b>
            <span>{turnSaid(ahead, t)}</span>
          </div>
          {after && (
            <p className="record-next">
              {t('record.then', { turn: turnSaid(after, t), metres: ahead.metres })}
            </p>
          )}
          <p className="record-body record-small">
            {t('record.left', { metres: rest, minutes: Math.max(1, Math.round(rest / 80)) })}
            <br />
            <span className="quiet">{t('record.quiet')}</span>
          </p>
        </>
      )}
      <div className="record-body">
        <button type="button" className="button" onClick={onEnd}>
          {t('record.endGuide')}
        </button>
      </div>
    </div>
  );
}

/** The recording looked over: its ways and stops, only those ticked added to the trip. */
function Review({
  trip,
  stop,
  fixes,
  parts,
  rests,
  said,
  onDone,
}: {
  trip: string;
  stop: PlanStop;
  fixes: Fix[];
  parts: ReturnType<typeof stretches>;
  rests: ReturnType<typeof stays>;
  said: 'added' | 'deleted' | 'failed' | null;
  onDone: (how: 'added' | 'deleted' | 'failed') => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [ways, setWays] = useState(() => parts.map(() => true));
  // Each stop: the day's thing it was at, when there is one within 150 m.
  const planned = rests.map(
    (rest) =>
      stop.agenda.find(
        (thing) => thing.place !== null && metresApart(rest, thing.place) <= 150,
      ) ?? null,
  );
  const [kept, setKept] = useState(() => planned.map((thing) => thing === null));
  const [names, setNames] = useState<(string | null)[]>(() => rests.map(() => null));
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    rests.forEach((rest, at) => {
      if (planned[at]) return;
      getHere(rest).then(
        (here) => {
          const name = here.address?.split(',').slice(0, 2).join(',') ?? null;
          setNames((was) => was.map((one, index) => (index === at ? name : one)));
        },
        () => undefined,
      );
    });
  }, [rests, planned]);
  const last = fixes.at(-1);
  const first = fixes[0];
  const length = (last?.at ?? 0) - (first?.at ?? 0);
  const km = (metres: number) =>
    `${(metres / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })} ${t('record.km')}`;

  const add = () => {
    const chosen = parts.filter((_, at) => ways[at] === true);
    const gpx = () =>
      gpxOf(
        chosen.flatMap((part) => fixes.slice(part.from, part.to + 1)),
        `${stop.city} ${toIso(new Date((chosen[0] as (typeof chosen)[number]).start))}`,
      );
    const work = [
      ...(chosen.length > 0
        ? [
            setTrack(
              trip,
              stop.id,
              new File([gpx()], 'recorded.gpx', { type: 'application/gpx+xml' }),
            ),
          ]
        : []),
      ...rests.flatMap((rest, at) =>
        kept[at] === true && planned[at] === null
          ? [
              addAgendaItem(trip, {
                stop: stop.id,
                name: names[at] ?? t('record.stayAt', { time: hhmm(rest.start) }),
                day: toIso(new Date(rest.start)),
                starts: hhmm(rest.start),
                ends: hhmm(rest.end),
                place: { lat: rest.lat, lon: rest.lon, name: names[at] ?? '' },
                price: null,
                currency: 'EUR',
                booked: false,
                link: null,
                by: null,
                byMinutes: null,
                parts: [],
              }),
            ]
          : [],
      ),
    ];
    Promise.all(work).then(
      () => {
        onDone('added');
      },
      () => {
        onDone('failed');
      },
    );
  };

  return (
    <div className="record-body">
      <p className="record-top">
        <span>{t('record.done')}</span>
        <span className="record-rec">{clock(length).slice(0, -3)}</span>
      </p>
      {parts.length === 0 && rests.length === 0 && (
        <p className="quiet">{t('record.nothing')}</p>
      )}
      {parts.length > 0 && (
        <fieldset className="record-points">
          <legend className="record-caps">{t('record.ways')}</legend>
          {parts.map((part, at) => (
            <label key={`${String(part.from)}-${String(part.to)}`} className="record-pt">
              <input
                type="checkbox"
                checked={ways[at] === true}
                onChange={() => {
                  setWays(ways.map((one, index) => (index === at ? !one : one)));
                }}
              />
              <span>
                {EMOJI[part.going]}{' '}
                {t('record.wayLine', {
                  going: t(`record.goingAs.${part.going}`),
                  km: km(part.metres),
                })}
                <small>
                  {t('record.wayWhen', {
                    from: hhmm(part.start),
                    to: hhmm(part.end),
                    sure: part.sure,
                  })}
                </small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {rests.length > 0 && (
        <fieldset className="record-points">
          <legend className="record-caps">{t('record.stays')}</legend>
          {rests.map((rest, at) => {
            const thing = planned[at];
            return (
              <label key={String(rest.start)} className="record-pt">
                <input
                  type="checkbox"
                  checked={kept[at] === true}
                  disabled={thing !== null}
                  onChange={() => {
                    setKept(kept.map((one, index) => (index === at ? !one : one)));
                  }}
                />
                <span>
                  {t('record.stayLine', {
                    name:
                      thing?.name ??
                      names[at] ??
                      t('record.stayAt', { time: hhmm(rest.start) }),
                    minutes: rest.minutes,
                  })}
                  <small>
                    {t('record.stayWhen', {
                      from: hhmm(rest.start),
                      to: hhmm(rest.end),
                      planned: t(thing ? 'record.inPlan' : 'record.notInPlan'),
                    })}
                  </small>
                </span>
              </label>
            );
          })}
        </fieldset>
      )}
      {said === 'failed' && (
        <p className="form-failed" role="alert">
          {t('record.failed')}
        </p>
      )}
      <div className="record-row">
        <button
          type="button"
          className="button button-quiet"
          onClick={() => {
            onDone('deleted');
          }}
        >
          {t('record.deleteAll')}
        </button>
        <button type="button" className="button button-primary" onClick={add}>
          {t('record.add')}
        </button>
      </div>
    </div>
  );
}
